<script>
import {
  createDirectoryItem,
  updateDirectoryItem,
  getDirectoryTree,
  getDirectoryChildren,
  addDirectoryChild,
  getDirectoryList,
} from "@/entities/directory";
import { Plus, FolderPlus, Folder, X, Check, Loader2 } from "lucide-vue-next";
import Button from "@/shared/ui/button/Button.vue";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import { FormSheet } from "@/shared/ui/form-sheet";

// Динамический компонент диалога
import DirectorySelectorDialog from "./DirectorySelectorDialog.vue";

export default {
  name: "DirectoryItemDrawer",

  components: {
    Plus,
    FolderPlus,
    Folder,
    X,
    Check,
    Loader2,
    DirectorySelectorDialog,
    Button,
    BaseTabs,
    FormSheet,
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
    FormItem,
    FormLabel,
    FormControl,
    Input,
  },

  props: {
    modelValue: Boolean,
    mode: {
      type: String,
      default: "add",
    },
    item: {
      type: Object,
      default: () => ({}),
    },
    parentInfo: {
      type: Object,
      default: null,
    },
  },

  emits: ["update:modelValue", "saved"],

  data() {
    return {
      visible: false,
      saving: false,
      selectedParent: null,
      selectedDirectory: null,

      // Для диалогов
      dialogType: "directories", // 'directories' или 'parents'
      dialogTitle: "",

      addMode: "create", // 'create' | 'existing'

      // Inline directory selector
      dirSearchQuery: "",
      dirSearchTimeout: null,
      dirItems: [],
      dirLoading: false,
      dirPagination: { page: 1, size: 20, total: 0 },

      form: this.getInitialForm(),
      categories: [],
      existingChildrenCodes: [],
    };
  },

  computed: {
    isAddMode() {
      return this.mode === "add";
    },

    isEditMode() {
      return this.mode === "edit";
    },

    drawerTitle() {
      if (this.isAddMode) {
        if (this.selectedDirectory) {
          return this.$t("Добавить существующий справочник");
        }
        return this.parentInfo
          ? this.$t("Добавить дочерний элемент")
          : this.$t("Создать новый элемент");
      }
      return this.$t("Редактировать элемент");
    },

    showParentField() {
      if (this.isAddMode && !this.parentInfo) return true;
      return this.isEditMode;
    },

    addModeTabs() {
      return {
        create: { label: this.$t("Создать новый") },
        existing: { label: this.$t("Выбрать существующий") },
      };
    },

    showDirectorySelectorButton() {
      return this.isAddMode && this.parentInfo && !this.selectedDirectory;
    },

    showFormFields() {
      if (this.isEditMode) return true;
      return this.addMode === "create";
    },

    finalParentCode() {
      return this.selectedParent
        ? this.selectedParent.code
        : this.parentInfo?.code || null;
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      async handler(val) {
        this.visible = val;
        if (val) {
          await this.resetForm();
          if (this.isEditMode && this.item) {
            this.loadItemData();
          }
          if (this.showParentField) {
            await this.loadCategories();
          }
          if (this.parentInfo) {
            await this.loadExistingChildren();
          }
        }
      },
    },

    parentInfo: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.selectedParent = {
            code: newVal.code,
            name: this.getDisplayName(newVal),
          };
        } else {
          this.selectedParent = null;
        }
      },
    },
  },

  methods: {
    getInitialForm() {
      return {
        code: "",
        name: "",
        name_kz: "",
        name_en: "",
        description: "",
        description_kz: "",
        description_en: "",
        is_subcategory: false,
        parent_code: null,
      };
    },

    getDisplayName(item) {
      if (!item) return "";
      return (
        item.name ||
        item.name_kz ||
        item.name_en ||
        item.code ||
        this.$t("Без названия")
      );
    },

    async loadExistingChildren() {
      if (!this.parentInfo?.code) return;

      try {
        const response = await getDirectoryChildren(this.parentInfo.code, {
          page: 1,
          size: 100,
        });

        if (response?.success && response.payload?.items) {
          this.existingChildrenCodes = response.payload.items.map(
            (item) => item.code
          );
        }
      } catch (error) {
        console.error("Error loading existing children:", error);
        this.existingChildrenCodes = [];
      }
    },

    async loadCategories() {
      try {
        const data = await getDirectoryTree({
          page: 1,
          size: 100,
          include_children: true,
        });
        if (data?.success && data.payload?.items) {
          this.categories = data.payload.items;
        }
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    },

    loadItemData() {
      const {
        code,
        name = "",
        name_kz = "",
        name_en = "",
        description = "",
        description_kz = "",
        description_en = "",
        is_subcategory = false,
        parent_code = null,
        parent = null,
      } = this.item;

      Object.assign(this.form, {
        code,
        name,
        name_kz,
        name_en,
        description,
        description_kz,
        description_en,
        is_subcategory,
        parent_code,
      });

      if (parent_code && parent) {
        this.selectedParent = {
          code: parent.code,
          name: this.getDisplayName(parent),
        };
      }
    },

    async resetForm() {
      this.form = this.getInitialForm();
      this.selectedParent = null;
      this.selectedDirectory = null;
      this.existingChildrenCodes = [];

      if (this.parentInfo) {
        this.form.parent_code = this.parentInfo.code;
      } else if (this.isEditMode && this.item?.parent_code) {
        this.form.parent_code = this.item.parent_code;
      } else {
        this.form.parent_code = null;
      }

      this.saving = false;
    },

    // Методы открытия диалогов
    openParentSelector() {
      this.dialogType = "parents";
      this.dialogTitle = this.$t("Выбор родительской категории");
      this.$refs.dialogRef.open();
    },

    openDirectorySelector() {
      this.dialogType = "directories";
      this.dialogTitle = this.$t("Выбор существующего справочника");
      this.$refs.dialogRef.open();
    },

    // Обработчик выбора из диалога
    handleDialogSelect(item) {
      if (this.dialogType === "parents") {
        this.selectedParent = item;
        this.form.parent_code = item?.code || null;
      } else {
        this.selectedDirectory = item;
      }
    },

    clearParent() {
      this.selectedParent = null;
      this.form.parent_code = null;
    },

    onAddModeChange(key) {
      this.addMode = key;
      if (key === "create") {
        this.selectedDirectory = null;
      } else if (key === "existing") {
        this.loadInlineDirectories();
      }
    },

    async loadInlineDirectories(page = 1) {
      this.dirLoading = true;
      this.dirPagination.page = page;
      try {
        const params = {
          page: this.dirPagination.page,
          size: this.dirPagination.size,
          parent_code: null,
        };
        if (this.dirSearchQuery) {
          params.q = this.dirSearchQuery;
        }
        const response = await getDirectoryList(params);
        if (response?.success && response.payload) {
          this.dirItems = (response.payload.items || []).filter(
            (dir) =>
              !this.existingChildrenCodes.includes(dir.code) &&
              dir.code !== this.parentInfo?.code
          );
          this.dirPagination.total = response.payload.total || 0;
        }
      } catch (error) {
        console.error("Error loading directories:", error);
        this.dirItems = [];
      } finally {
        this.dirLoading = false;
      }
    },

    onDirPageChange(page) {
      this.loadInlineDirectories(page);
    },

    onDirSearch() {
      clearTimeout(this.dirSearchTimeout);
      this.dirSearchTimeout = setTimeout(() => {
        this.loadInlineDirectories();
      }, 400);
    },

    selectInlineDirectory(item) {
      this.selectedDirectory = item;
    },

    clearDirectorySelection() {
      this.selectedDirectory = null;
      this.addMode = "create";
    },

    // Отправка формы
    async submitForm() {
      if (this.selectedDirectory) {
        await this.addExistingDirectory();
      } else {
        await this.createOrUpdateDirectory();
      }
    },

    async addExistingDirectory() {
      if (!this.selectedDirectory || !this.parentInfo?.code) {
        this.$message.error(this.$t("Не выбран справочник для добавления"));
        return;
      }

      this.saving = true;
      try {
        const result = await addDirectoryChild(
          this.parentInfo.code,
          this.selectedDirectory.code
        );

        if (result?.success) {
          this.$message.success(
            this.$t("Справочник успешно добавлен как дочерний")
          );
          this.$emit("saved");
          this.closeDrawer();
        } else {
          this.$message.error(
            result?.message || this.$t("Не удалось добавить справочник")
          );
        }
      } catch (error) {
        console.error("Error adding existing directory:", error);
        this.handleApiError(error);
      } finally {
        this.saving = false;
      }
    },

    async createOrUpdateDirectory() {
      if (!(await this.validateForm())) {
        return;
      }

      this.saving = true;
      try {
        const payload = this.preparePayload();
        let result;

        if (this.isAddMode) {
          result = await createDirectoryItem(payload);
        } else {
          const { ...updateData } = payload;
          result = await updateDirectoryItem(this.item.code, updateData);
        }

        if (result?.success) {
          this.handleSuccess();
          this.closeDrawer();
        } else {
          this.handleSaveError(result);
        }
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.saving = false;
      }
    },

    async validateForm() {
      return true;
    },

    preparePayload() {
      const payload = {
        code: this.form.code,
        name: this.form.name,
        name_kz: this.form.name_kz || null,
        name_en: this.form.name_en || null,
        description: this.form.description || null,
        description_kz: this.form.description_kz || null,
        description_en: this.form.description_en || null,
        is_subcategory: this.form.is_subcategory,
        parent_code: this.finalParentCode,
      };

      return payload;
    },

    handleSuccess() {
      const message = this.isAddMode
        ? this.$t("Элемент успешно создан")
        : this.$t("Элемент успешно обновлен");

      this.$message.success(message);
      this.$emit("saved");
    },

    handleSaveError(result) {
      const errorMessage =
        result?.message || this.$t("Не удалось сохранить элемент");
      this.$message.error(errorMessage);
    },

    handleApiError(error) {
      console.error("API Error:", error);

      if (!error.response) {
        this.$message.error(this.$t("Ошибка соединения с сервером"));
        return;
      }

      const { status, data } = error.response;

      const errorHandlers = {
        400: () => this.handleBadRequest(data),
        401: () => this.$message.error(this.$t("Сессия истекла")),
        403: () => this.$message.error(this.$t("Доступ запрещен")),
        404: () => this.$message.error(this.$t("Элемент не найден")),
        409: () =>
          this.$message.error(this.$t("Элемент с таким кодом уже существует")),
        422: () => this.handleValidationError(data),
        500: () => this.$message.error(this.$t("Внутренняя ошибка сервера")),
      };

      const handler =
        errorHandlers[status] || (() => this.handleGenericError(data));
      handler();
    },

    handleBadRequest(data) {
      const detail =
        data?.detail || data?.message || this.$t("Некорректные данные");
      this.$message.error(detail);
    },

    handleValidationError(data) {
      const validationErrors = data?.detail || [];
      if (Array.isArray(validationErrors)) {
        const errorMessages = validationErrors
          .map((err) => `${err.loc?.join(".") || ""}: ${err.msg}`)
          .join("\n");
        this.$message.error(
          errorMessages || this.$t("Ошибка валидации данных")
        );
      }
    },

    handleGenericError(data) {
      this.$message.error(data?.message || this.$t("Произошла ошибка"));
    },

    closeDrawer() {
      this.resetForm();
      this.$emit("update:modelValue", false);
      this.visible = false;
    },
  },
};
</script>

<template>
  <FormSheet
    v-model:open="visible"
    :title="drawerTitle"
    :width="650"
    @close="closeDrawer"
  >
    <div class="w-full h-full flex flex-col gap-6">
      <main class="flex flex-col gap-2 flex-1">
        <div v-if="showDirectorySelectorButton" class="mb-6">
          <BaseTabs
            :tabs="addModeTabs"
            :active-tab="addMode"
            @change:tab="onAddModeChange"
          />
        </div>

        <!-- Selected directory card -->
        <div
          v-if="selectedDirectory && isAddMode"
          class="px-3 py-2.5 bg-muted border border-border rounded-md"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2.5">
              <div
                class="w-8 h-8 rounded-md bg-background flex items-center justify-center shrink-0"
              >
                <Folder class="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <div>
                <div class="text-sm font-medium text-foreground">
                  {{ getDisplayName(selectedDirectory) }}
                </div>
                <div class="text-xs text-muted-foreground mt-0.5">
                  {{ $t("Код") }}: {{ selectedDirectory.code }}
                </div>
              </div>
            </div>
            <button
              type="button"
              class="p-1.5 hover:bg-card rounded-md transition-colors"
              @click="clearDirectorySelection"
            >
              <X class="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </div>
        </div>

        <!-- Inline directory selector -->
        <div
          v-if="addMode === 'existing' && showDirectorySelectorButton"
          class="flex flex-col gap-2 flex-1 min-h-0"
        >
          <Input
            v-model="dirSearchQuery"
            :placeholder="$t('Поиск по названию или коду...')"
            class="w-full mb-2 text-sm"
            @input="onDirSearch"
          />

          <div v-if="dirLoading" class="flex items-center justify-center py-6">
            <Loader2 class="w-4 h-4 animate-spin text-muted-foreground" />
          </div>

          <div
            v-else-if="dirItems.length === 0"
            class="text-center py-6 text-sm text-muted-foreground"
          >
            {{ $t("Нет доступных элементов") }}
          </div>

          <div
            v-else
            class="flex flex-col gap-1.5 flex-1 min-h-0 overflow-y-auto"
          >
            <div
              v-for="dir in dirItems"
              :key="dir.code"
              class="dir-item"
              :class="{
                'dir-item--active': selectedDirectory?.code === dir.code,
              }"
              @click="selectInlineDirectory(dir)"
            >
              <div class="flex items-center gap-2.5">
                <div
                  class="w-8 h-8 rounded-md bg-muted flex items-center justify-center shrink-0"
                >
                  <Folder class="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                <div class="min-w-0">
                  <div class="text-sm font-medium text-foreground truncate">
                    {{ getDisplayName(dir) }}
                  </div>
                  <div class="text-xs text-muted-foreground mt-0.5 truncate">
                    {{ dir.code }}
                  </div>
                </div>
                <Check
                  v-if="selectedDirectory?.code === dir.code"
                  class="w-3.5 h-3.5 ml-auto shrink-0 text-primary"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="showParentField" class="mb-4">
          <div class="text-sm font-medium text-foreground mb-2">
            {{ $t("Родительская категория") }}
          </div>
          <div class="flex flex-col gap-2">
            <div
              v-if="selectedParent"
              class="px-3 py-2.5 bg-muted border border-border rounded-md"
            >
              <div class="flex items-center justify-between">
                <div>
                  <div class="text-sm font-medium text-foreground">
                    {{ selectedParent.name || selectedParent.code }}
                  </div>
                  <div class="text-xs text-muted-foreground mt-0.5">
                    {{ $t("Код") }}: {{ selectedParent.code }}
                  </div>
                </div>
                <button
                  type="button"
                  class="p-1.5 hover:bg-card rounded-md transition-colors"
                  @click="clearParent"
                >
                  <X class="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </div>
            </div>

            <button
              v-if="!selectedParent"
              type="button"
              class="w-full px-3 py-3 border border-dashed border-border rounded-md hover:border-primary/40 hover:bg-accent transition-colors text-center"
              @click="openParentSelector"
            >
              <div class="flex flex-col items-center gap-1">
                <span class="text-sm font-medium text-foreground">
                  {{ $t("Выбрать родительскую категорию") }}
                </span>
                <span class="text-xs text-muted-foreground">
                  {{ $t("Оставьте пустым для корневого элемента") }}
                </span>
              </div>
            </button>
          </div>
        </div>

        <div v-if="showFormFields" class="flex flex-col gap-3">
          <div v-if="!parentInfo || !selectedDirectory">
            <FormItem float>
              <FormLabel>{{ $t("Уникальный код") }}</FormLabel>
              <FormControl>
                <Input
                  v-model="form.code"
                  :placeholder="
                    $t('Только латинские буквы, цифры и подчеркивание')
                  "
                  :disabled="isEditMode"
                  class="h-10"
                />
              </FormControl>
            </FormItem>
          </div>

          <FormItem float v-if="!parentInfo || !selectedDirectory">
            <FormLabel>{{ $t("Наименование (рус)") }}</FormLabel>
            <FormControl>
              <Input v-model="form.name" class="h-10" />
            </FormControl>
          </FormItem>

          <FormItem float v-if="!parentInfo || !selectedDirectory">
            <FormLabel>{{ $t("Наименование (қаз)") }}</FormLabel>
            <FormControl>
              <Input v-model="form.name_kz" class="h-10" />
            </FormControl>
          </FormItem>

          <FormItem float v-if="!parentInfo || !selectedDirectory">
            <FormLabel>{{ $t("Наименование (англ)") }}</FormLabel>
            <FormControl>
              <Input v-model="form.name_en" class="h-10" />
            </FormControl>
          </FormItem>
        </div>
      </main>

      <DirectorySelectorDialog
        ref="dialogRef"
        :type="dialogType"
        :title="dialogTitle"
        :exclude-codes="
          dialogType === 'directories' ? existingChildrenCodes : []
        "
        :exclude-parent-code="
          dialogType === 'directories' ? parentInfo?.code : null
        "
        :parent-categories="dialogType === 'parents' ? categories : []"
        @select="handleDialogSelect"
      />
    </div>

    <template #footer>
      <Pagination
        v-if="
          addMode === 'existing' &&
          !selectedDirectory &&
          dirPagination.total > dirPagination.size
        "
        :total="dirPagination.total"
        :items-per-page="dirPagination.size"
        :page="dirPagination.page"
        :sibling-count="1"
        show-edges
        class="w-auto mx-0"
        @update:page="onDirPageChange"
      >
        <PaginationContent v-slot="{ items: pageItems }">
          <PaginationPrevious />
          <template v-for="(item, index) in pageItems" :key="index">
            <PaginationItem
              v-if="item.type === 'page'"
              :value="item.value"
              :is-active="item.value === dirPagination.page"
            >
              {{ item.value }}
            </PaginationItem>
            <PaginationEllipsis v-else />
          </template>
          <PaginationNext />
        </PaginationContent>
      </Pagination>
      <div class="flex-1"></div>
      <Button variant="outline" size="sm" @click="closeDrawer">
        {{ $t("Отмена") }}
      </Button>
      <Button class="ml-2" variant="default" size="sm" @click="submitForm">
        {{
          addMode === "existing"
            ? $t("Добавить")
            : isAddMode
            ? $t("Создать")
            : $t("Сохранить")
        }}
      </Button>
    </template>
  </FormSheet>
</template>

<style scoped lang="scss">
.dir-item {
  @apply px-3 py-2.5 rounded-md border border-border cursor-pointer transition-colors shrink-0;

  &:hover {
    @apply border-primary/40 bg-accent;
  }

  &--active {
    @apply border-primary bg-accent;
  }
}
</style>
